/* eslint-disable react/no-array-index-key */
import {
  parseISO, format, isSameDay, sub, add, endOfDay, startOfDay,
} from 'date-fns';
import {
  PieChart,
  Pie,
  Tooltip,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Sector,
} from 'recharts';
import { useState } from 'react';
import useBackups from '../../hooks/useBackups';
import {
  PieChartsContainer, Container, Column, ChartLabel,
} from './styles';
import theme from '../../assets/styles/themes/default';
import { error, success, warning } from '../../utils/translateResult';
import useQueryReports from '../../hooks/useQueryReports';
import Loader from '../../components/Loader';

const renderActiveShape = (props) => {
  const RADIAN = Math.PI / 180;
  const {
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    percent,
    value,
  } = props;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius + 10) * cos;
  const sy = cy + (outerRadius + 10) * sin;
  const mx = cx + (outerRadius + 30) * cos;
  const my = cy + (outerRadius + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
  const ey = my;
  const textAnchor = cos >= 0 ? 'start' : 'end';

  return (
    <g>
      <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill}>
        {payload.name}
      </text>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 6}
        outerRadius={outerRadius + 10}
        fill={fill}
      />
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke={fill}
        fill="none"
      />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        textAnchor={textAnchor}
        fill="#333"
      >
        {value}
      </text>
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        dy={18}
        textAnchor={textAnchor}
        fill="#999"
      >
        {`${(percent * 100).toFixed(2)}%`}
      </text>
    </g>
  );
};

function getLastSevenDays() {
  const date = Date.now();
  const first = sub(date, { days: 6 });

  const arr = Array.from({ length: 7 }, (_, index) => {
    if (index === 0) {
      return startOfDay(first);
    }
    if (index === 6) {
      const last = add(first, { days: index });
      return endOfDay(last);
    }

    return add(first, { days: index });
  });
  return arr;
}

export default function OverviewPage() {
  const { backups } = useBackups();
  const [backupsReportsIndex, setBackupsReportsIndex] = useState(0);
  const [weeklyReportsIndex, setWeeklyReportsIndex] = useState(0);

  const lastSevenDays = getLastSevenDays();
  const dateInterval = lastSevenDays
    .filter((_, index) => index === 0 || index === 6)
    .map((value) => format(value, 'yyyy-MM-dd'));

  const { isFetching, data } = useQueryReports({
    resume: true,
    dateInterval,
  });

  const backupsReports = [
    {
      name: 'Successo',
      value: 0,
      color: '#27AE60',
      alias: 'Success',
      index: 0,
    },
    {
      name: 'Alerta',
      value: 0,
      color: '#FAB005',
      alias: 'Warning',
      index: 1,
    },
    {
      name: 'Erro',
      value: 0,
      color: '#EB5757',
      alias: 'Error',
      index: 2,
    },
  ];

  const weeklyReports = [
    { name: 'Successo', value: 0, color: '#27AE60' },
    { name: 'Alerta', value: 0, color: '#FAB005' },
    { name: 'Erro', value: 0, color: '#EB5757' },
  ];

  function incrementReportCount(arr, report) {
    const index = arr.findIndex((obj) => obj.alias === report.result);
    const object = arr[index];
    object.value += 1;

    arr.splice(index, 1);
    arr.push(object);
    arr.sort((a, b) => a.index - b.index);
  }

  const bars = lastSevenDays.map((date) => ({
    name: format(date, 'dd/MM/yyyy'),
    sucesso: data?.reports?.filter(
      ({ message }) => message.parsedResult === success
        && isSameDay(date, parseISO(message.endTime)),
    ).length,
    alerta: data?.reports?.filter(
      ({ message }) => message.parsedResult === warning
        && isSameDay(date, parseISO(message.endTime)),
    ).length,
    erro: data?.reports?.filter(
      ({ message }) => message.parsedResult === error && isSameDay(date, parseISO(message.endTime)),
    ).length,
  }));

  bars.forEach((bar) => {
    weeklyReports[0].value += bar.sucesso;
    weeklyReports[1].value += bar.alerta;
    weeklyReports[2].value += bar.erro;
  });

  backups
    .map((backup) => backup.reports[0])
    .filter((report) => report !== undefined)
    .forEach((report) => incrementReportCount(backupsReports, report));

  const sumOfWeeklyReports = weeklyReports.reduce(
    (acc, curr) => acc + curr.value,
    0,
  );

  return (
    <Container>
      <h1>Visão Geral</h1>
      {isFetching ? (
        <Loader />
      ) : (
        <>
          <PieChartsContainer>
            <Column>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    activeIndex={backupsReportsIndex}
                    activeShape={renderActiveShape}
                    onMouseEnter={(_, index) => setBackupsReportsIndex(index)}
                    data={backupsReports}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {backupsReports.map((element, index) => (
                      <Cell key={`cell-${index}`} fill={element.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <ChartLabel>Backups</ChartLabel>
            </Column>
            {/* <Column>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    activeIndex={backupsReportsIndex}
                    activeShape={renderActiveShape}
                    onMouseEnter={(_, index) => setBackupsReportsIndex(index)}
                    data={backupsReports}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <ChartLabel>Backups com relatórios recentes</ChartLabel>
            </Column> */}
            <Column>
              <ResponsiveContainer>
                <PieChart>
                  {sumOfWeeklyReports > 0 ? (
                    <Pie
                      dataKey="value"
                      activeIndex={weeklyReportsIndex}
                      activeShape={renderActiveShape}
                      onMouseEnter={(_, index) => setWeeklyReportsIndex(index)}
                      data={weeklyReports}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      fill="#8884d8"
                    >
                      {weeklyReports.map((element, index) => (
                        <Cell key={`cell-${index}`} fill={element.color} />
                      ))}
                    </Pie>
                  ) : (
                    <Pie
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      data={[{ name: 'No Data', value: 1 }]}
                      fill="#eeeeee"
                    />
                  )}
                  {sumOfWeeklyReports > 0 ? <Tooltip /> : null}
                </PieChart>
              </ResponsiveContainer>
              <ChartLabel>Relatórios nessa semana</ChartLabel>
            </Column>
          </PieChartsContainer>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={bars}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="sucesso" stackId="a" fill={theme.colors.success} />
              <Bar dataKey="alerta" stackId="a" fill={theme.colors.warning} />
              <Bar dataKey="erro" stackId="a" fill={theme.colors.error} />
            </BarChart>
          </ResponsiveContainer>
        </>
      )}
    </Container>
  );
}
